import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import type { BorderWidthModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { LengthInputSelector } from "@editor/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import DirectionBottom from "@svg/modifiers/direction/direction-bottom";
import DirectionLeft from "@svg/modifiers/direction/direction-left";
import DirectionRight from "@svg/modifiers/direction/direction-right";
import DirectionTop from "@svg/modifiers/direction/direction-top";
import DirectionUnified from "@svg/modifiers/direction/direction-unified";
import ExpandModifierOptions from "@svg/modifiers/expand-modifier-options";

import { useSelectedElement } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import IconButton from "@replo/design-system/components/button/IconButton";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { parseUnit } from "replo-runtime/shared/utils/units";
import { REPLO_MIXED_STYLE_VALUE } from "replo-runtime/store/utils/mixed-values";

type BorderMode = "unified" | "individual";

const BORDER_WIDTH_OPTIONS = [
  { label: "None", value: "0" },
  { label: "1px", value: "1" },
  { label: "2px", value: "2" },
  { label: "4px", value: "4" },
  { label: "6px", value: "6" },
  { label: "8px", value: "8" },
];

interface BorderValue {
  top: number | null;
  right: number | null;
  bottom: number | null;
  left: number | null;
}

interface BorderWidthModifierProps {
  onChange: (modifiers: BorderWidthModifierValue[]) => void;
}

const COMMON_LENGTH_INPUT_PROPS = {
  placeholder: "0px",
  metrics: ["px"],
  unitDefaults: { value: "0", unit: "px" },
  menuOptions: BORDER_WIDTH_OPTIONS,
  inputClassName: "w-full",
  endEnhancer: () => <ChevronDown size={12} />,
};

const getInitialWidthMode = (borderValue: BorderValue | null): BorderMode => {
  if (!borderValue) {
    return "unified";
  }

  const { top, right, bottom, left } = borderValue;
  if (top === right && right === bottom && bottom === left) {
    return "unified";
  }

  return "individual";
};

interface BorderWidthInputProps {
  direction: "top" | "right" | "bottom" | "left";
  value: number | null | undefined;
  onChange: (value: number | undefined) => void;
  icon?: React.ReactNode;
  displayMixed?: boolean;
}

const BorderWidthInput: React.FC<BorderWidthInputProps> = ({
  value,
  onChange,
  icon,
  displayMixed,
}) => {
  let valueProp: string | ReploMixedStyleValue | null;
  if (displayMixed) {
    valueProp = REPLO_MIXED_STYLE_VALUE as ReploMixedStyleValue;
  } else if (value != null) {
    valueProp = `${value}px`;
  } else {
    valueProp = null;
  }

  return (
    <LengthInputSelector
      field="borderWidth"
      value={valueProp}
      onChange={(newValue) => {
        const result = parseUnit(
          newValue,
          { value: 0, unit: "px" },
          "default",
          "px",
        );
        const numericValue =
          typeof result.value === "number"
            ? result.value
            : Number.parseFloat(result.value);
        onChange(numericValue);
      }}
      startEnhancer={icon}
      {...COMMON_LENGTH_INPUT_PROPS}
    />
  );
};

const BorderWidthModifier: React.FC<BorderWidthModifierProps> = ({
  onChange,
}) => {
  const selectedElement = useSelectedElement();

  const extractedBorderWidths = useMemo(() => {
    const widths = selectedElement
      ? {
          top: selectedElement.styleOverrides?.borderTopWidth ?? null,
          right: selectedElement.styleOverrides?.borderRightWidth ?? null,
          bottom: selectedElement.styleOverrides?.borderBottomWidth ?? null,
          left: selectedElement.styleOverrides?.borderLeftWidth ?? null,
        }
      : null;

    return widths;
  }, [selectedElement]);

  const [currentWidths, setCurrentWidths] = useOverridableState<BorderValue>(
    extractedBorderWidths ?? {
      top: null,
      right: null,
      bottom: null,
      left: null,
    },
  );

  const [widthMode, setWidthMode] = useState<BorderMode>(() =>
    getInitialWidthMode(currentWidths),
  );

  useEffect(() => {
    setWidthMode(getInitialWidthMode(extractedBorderWidths));
  }, [extractedBorderWidths]);

  const updateBorderWidth = useCallback(
    (updates: Partial<BorderValue>) => {
      const newValue: BorderValue = { ...currentWidths, ...updates };
      setCurrentWidths(newValue);

      const modifiers: BorderWidthModifierValue[] = [];

      if (updates.top !== undefined) {
        modifiers.push({
          type: "borderWidth",
          direction: "top",
          value: updates.top as number,
        });
      }
      if (updates.right !== undefined) {
        modifiers.push({
          type: "borderWidth",
          direction: "right",
          value: updates.right as number,
        });
      }
      if (updates.bottom !== undefined) {
        modifiers.push({
          type: "borderWidth",
          direction: "bottom",
          value: updates.bottom as number,
        });
      }
      if (updates.left !== undefined) {
        modifiers.push({
          type: "borderWidth",
          direction: "left",
          value: updates.left as number,
        });
      }

      onChange(modifiers);
    },
    [currentWidths, setCurrentWidths, onChange],
  );

  const widthInputConfigs: Record<
    BorderMode,
    Array<{
      key: string;
      label: string;
      value: number | null | undefined;
      onChange: (newValue: number | undefined) => void;
      icon?: React.ReactNode;
      displayMixed?: boolean;
    }>
  > = useMemo(() => {
    // NOTE (Kevin 08-08-2025): When switching to unified mode, show "Mixed" if any side has a value
    // and the four sides are not all equal.
    const { top, right, bottom, left } = currentWidths ?? {};
    const isUnifiedMixed =
      [top, right, bottom, left].some((v) => v != null) &&
      !(top === right && right === bottom && bottom === left);

    return {
      unified: [
        {
          key: "unified",
          label: "All Sides",
          icon: <DirectionUnified className="w-4 h-4" />,
          value: top,
          displayMixed: Boolean(isUnifiedMixed),
          onChange: (newValue) => {
            updateBorderWidth({
              top: newValue,
              right: newValue,
              bottom: newValue,
              left: newValue,
            });
          },
        },
      ],
      individual: [
        {
          key: "top",
          label: "Top",
          icon: <DirectionTop className="w-4 h-4" />,
          value: top,
          onChange: (newValue) => {
            updateBorderWidth({ top: newValue });
          },
        },
        {
          key: "right",
          label: "Right",
          icon: <DirectionRight className="w-4 h-4" />,
          value: right,
          onChange: (newValue) => {
            updateBorderWidth({ right: newValue });
          },
        },
        {
          key: "bottom",
          label: "Bottom",
          icon: <DirectionBottom className="w-4 h-4" />,
          value: bottom,
          onChange: (newValue) => {
            updateBorderWidth({ bottom: newValue });
          },
        },
        {
          key: "left",
          label: "Left",
          icon: <DirectionLeft className="w-4 h-4" />,
          value: left,
          onChange: (newValue) => {
            updateBorderWidth({ left: newValue });
          },
        },
      ],
    };
  }, [currentWidths, updateBorderWidth]);

  const widthInputs = widthInputConfigs[widthMode];

  const handleWidthModeToggle = () => {
    setWidthMode(widthMode === "unified" ? "individual" : "unified");
  };

  return (
    <div className="border-width-modifier">
      {widthMode === "unified" ? (
        <div className="grid grid-cols-[1fr_min-content] gap-2 items-center">
          {widthInputs.map((input) => (
            <BorderWidthInput
              key={input.key}
              direction={input.key as any}
              value={input.value}
              onChange={(v) => {
                input.onChange(v);
              }}
              icon={input.icon}
              displayMixed={input.displayMixed}
            />
          ))}
          <IconButton
            variant="secondary"
            size="sm"
            onClick={handleWidthModeToggle}
            tooltipText="Set individual sides"
            icon={<ExpandModifierOptions className="w-3 h-3" />}
          />
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-center">
          {/* First row: Top and Right */}
          {widthInputs.slice(0, 2).map((input) => (
            <BorderWidthInput
              key={input.key}
              direction={input.key as any}
              value={input.value}
              onChange={input.onChange}
              icon={input.icon}
              displayMixed={input.displayMixed}
            />
          ))}
          <IconButton
            variant="secondary"
            size="sm"
            onClick={handleWidthModeToggle}
            tooltipText="Use unified width"
            icon={<ExpandModifierOptions className="w-3 h-3" />}
          />
          {/* Second row: Bottom and Left */}
          {widthInputs.slice(2).map((input) => (
            <BorderWidthInput
              key={input.key}
              direction={input.key as any}
              value={input.value}
              onChange={input.onChange}
              icon={input.icon}
              displayMixed={input.displayMixed}
            />
          ))}
          <div></div> {/* Empty space to align with button column */}
        </div>
      )}
    </div>
  );
};

export default BorderWidthModifier;
